import { GQLProductFragment, GQLStoreFragment } from '@theprintbar/graphql';
import { Merch, cn, getSortedColours, getSortedVariantsByColour } from '@theprintbar/shared';
import { useCallback, useState } from 'react';
import { CustomColourIconHandler, ProductColourSwatch } from '../Product';

interface ProductColoursProps {
	merch?: Merch;
	product: GQLProductFragment;
	loading?: boolean;
	store?: GQLStoreFragment | null;
	updateMerch: (merch: Merch) => void;
	available?: boolean;
	customColourIconHandler?: CustomColourIconHandler;
	freeEdit?: boolean;
}

export function ProductColours({
	merch,
	loading,
	product,
	freeEdit,
	store,
	customColourIconHandler,
	available,
	updateMerch,
}: ProductColoursProps) {
	const activeCode = merch?.colour || getSortedColours(product.colours)[0]?.colour.code;
	const [hoveredColour, setHoveredColour] = useState<string>();

	const currentProductColour = product.colours.find(c =>
		hoveredColour ? c.colour.code === hoveredColour : c.colour.code === activeCode,
	);

	const handleColourChange = useCallback(
		(newColourCode?: string) => {
			if (merch && newColourCode && newColourCode !== merch.colour) {
				const colour = product.colours.find(c => c.colour.code === newColourCode);
				if (merch.sameday && !colour?.isSameday) return;

				const colourVariants = getSortedVariantsByColour(product, newColourCode);

				updateMerch({
					...merch,
					dirty: true,
					colour: newColourCode,
					sizing: merch.sizing.filter(s => colourVariants.some(v => v.stocked && v.variant.size.code === s.size)),
				});
			}
		},
		[product, merch, updateMerch],
	);

	const colourName = product.customColourName || 'Colour';

	return (
		<div className="space-y-2.5">
			<div className="flex items-end">
				<h5 className="uppercase text-xs whitespace-nowrap">{available ? `Available ${colourName}s` : colourName}</h5>
				<p className="leading-none">
					{currentProductColour?.colour && !loading && (
						<span className="text-blue text-xs leading-tight">: {currentProductColour.colour.name}</span>
					)}
					{!!currentProductColour && !loading && !currentProductColour.inStock ? (
						<span className="text-grey text-xs leading-tight"> - Out of stock</span>
					) : (
						''
					)}
				</p>
			</div>
			<div className={cn(loading && 'opacity-50 pointer-events-none')}>
				<ProductColourSwatch
					samedayOnly={!store && merch?.sameday}
					colours={product.colours.filter(c => freeEdit || c.colour.code === activeCode)}
					hideName
					hideLightningBolt={!!store}
					max={15}
					renderAllOnMoreClick
					onClick={colour => handleColourChange(colour?.colour.code)}
					activeCode={loading ? undefined : activeCode}
					onHover={c => setHoveredColour(c?.colour.code)}
					customColourIconHandler={customColourIconHandler}
				/>
			</div>
		</div>
	);
}
